import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, Minus, Sparkles } from "lucide-react";
import Footer from "../components/Footer";
import { planPriceLabel } from "../lib/planPrice";
import { t } from "../i18n";

export default function PlansUpgrade() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);

  useEffect(() => {
    api.get("/plans/public").then(({ data }) => setData(data)).catch(() => {});
    api.get("/me/recursos").then(({ data }) => setMine(data)).catch(() => {});
  }, []);

  if (!data) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  const cell = (pid, rid) => (data.valores[pid] || {})[rid] || {};

  const groupedRecursos = (() => {
    const order = [];
    const buckets = {};
    for (const r of data.recursos) {
      const cat = r.categoria || t("Outros");
      if (!buckets[cat]) { buckets[cat] = []; order.push(cat); }
      buckets[cat].push(r);
    }
    return order.flatMap((cat) => buckets[cat]);
  })();

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="h-16 border-b border-border/60 flex items-center gap-3 px-6 md:px-10 bg-card">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="upgrade-back"><ArrowLeft className="h-4 w-4 mr-1" />{t("Voltar")}</Button>
        <span className="font-display font-bold">{t("Planos")}</span>
      </header>
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">{t("Escolha seu plano")}</h1>
          <p className="text-muted-foreground mt-3">{t("Desbloqueie integrações ao vivo, mais projetos e armazenamento.")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5" data-testid="upgrade-plans">
          {data.planos.map((p, idx) => {
            const atual = mine?.plano_id === p.id;
            const destaque = idx === 1;
            return (
              <div key={p.id} className={`rounded-xl border p-6 flex flex-col ${destaque ? "border-primary shadow-[0_0_40px_-12px_var(--primary)]" : "border-border/60"} bg-card`} data-testid={`upgrade-plan-${p.id}`}>
                {destaque && <span className="self-start text-[10px] font-bold uppercase tracking-widest text-primary flex items-center gap-1 mb-2"><Sparkles className="h-3 w-3" />Popular</span>}
                <h3 className="font-display font-extrabold text-2xl">{p.nome}</h3>
                <div className="mt-2 mb-5"><span className="text-4xl font-extrabold">{planPriceLabel(p)}</span><span className="text-muted-foreground text-sm">{t("/mês")}</span></div>
                <ul className="space-y-2 flex-1">
                  {groupedRecursos.map((r) => {
                    const c = cell(p.id, r.id);
                    if (r.tipo === "numero") return <li key={r.id} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500 flex-shrink-0" /><span>{c.valor_numero ?? 0} · {r.nome_exibicao}</span></li>;
                    return <li key={r.id} className={`flex items-center gap-2 text-sm ${c.valor_boolean ? "" : "text-muted-foreground/50"}`}>{c.valor_boolean ? <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" /> : <Minus className="h-4 w-4 flex-shrink-0" />}<span>{r.nome_exibicao}</span></li>;
                  })}
                </ul>
                <Button className="w-full mt-6 rounded-md" variant={atual ? "secondary" : "default"} disabled={atual} onClick={() => { const dest = p.cta_url && p.cta_url.trim(); if (dest) window.open(dest, dest.startsWith("http") ? "_blank" : "_self"); else window.open(`mailto:agsm.livegrafx@gmail.com?subject=${encodeURIComponent(t("Upgrade para {nome}", { nome: p.nome }))}`, "_blank"); }} data-testid={`upgrade-cta-${p.id}`}>
                  {atual ? t("Plano atual") : t("Assinar {nome}", { nome: p.nome })}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">{t("A ativação é feita manualmente pela nossa equipe após o pagamento. Em breve: assinatura automática.")}</p>
      </main>
      <Footer />
    </div>
  );
}
